import { DotIcon } from "@/assets/icons";
import { Divider } from "@/components/ui/Divider";
import { COUNTRY_NAME } from "@/constants/dataset";
import { DatasetExploreItem } from "@/types/dataset";
import { LayerType } from "@/types/map";

import { ChevronDown } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { SPATIAL_INFO } from "../dataset/datasetCard";

type Props = {
  data: DatasetExploreItem;
  expanded: boolean;
  handleExpandCollapse: (data: DatasetExploreItem) => void;
};

export const DatasetCard = ({
  data,
  expanded,
  handleExpandCollapse
}: Props) => {
  const { label, icon, textColor } =
    SPATIAL_INFO[data.spatial_aggregation_type] ||
    SPATIAL_INFO[LayerType.POLYGON];

  return (
    <div
      className={twMerge(
        "group border rounded-lg bg-white-smoke",
        !expanded && "border-none"
      )}
    >
      <button
        className={twMerge(
          "flex flex-col font-sans border-[1px] rounded-lg p-4 gap-2 min-h-[114px] cursor-pointer justify-between relative transition-colors w-full",
          expanded ? "bg-teal-50 border-teal-200" : "bg-white border-gainsboro",
          "hover:bg-teal-50 hover:border-teal-200"
        )}
        onClick={() => handleExpandCollapse(data)}
        tabIndex={0}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0">
          <div className="flex gap-2 items-center">
            <div className="h-10 w-10 border rounded-lg flex items-center justify-center bg-white">
              {data?.provider?.logo_asset_url ? (
                <img
                  src={data.provider.logo_asset_url}
                  alt={`logo-${data.source}`}
                  className="h-8 w-8 object-contain"
                />
              ) : null}
            </div>
            <div className="flex items-start flex-col gap-1 flex-1 min-w-0">
              <p
                className={twMerge(
                  "text-sm text-txtHeading font-bold truncate",
                  expanded && "text-teal-500"
                )}
                title={data.name}
              >
                {data.name}
              </p>
              <p
                className="text-[10px] truncate text-txtDescription"
                title={data.short_description}
              >
                {data.short_description}
              </p>
            </div>
          </div>
          <ChevronDown
            className={twMerge(
              "transition-transform duration-200",
              expanded && "rotate-180"
            )}
          />
        </div>

        {/* Metadata bar */}
        <div className="flex flex-col gap-1">
          <Divider
            className="bg-gainsboro w-full h-[1px]"
            lineClassName={twMerge(
              "group-hover:border-teal-200",
              expanded && "border-teal-200"
            )}
          />
          <div className="flex justify-between mt-1">
            <div className="flex items-center gap-1">
              {icon}
              <span className={twMerge("text-[10px]", textColor)}>{label}</span>
              <div className="flex items-center gap-1 px-2">
                <DotIcon className="fill-light-gray" />
                <span className="text-[10px] text-grey">
                  {COUNTRY_NAME[data?.country_id || "id"]}
                </span>
                <DotIcon className="fill-light-gray" />
                <span className="text-[10px] text-grey">Nationwide</span>
              </div>
            </div>
            <span className="text-[10px] text-grey">BVT Data</span>
          </div>
        </div>
      </button>
      {/* Expanded content */}
      {expanded && (
        <div className="p-4 text-[11px] text-txtHeading">
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-[2fr_3fr]">
              <p className="font-semibold">Adm Area Selection:</p>
              <p>Indonesia → DKI Jakarta → Jakarta Pusat</p>
            </div>
            <div className="grid grid-cols-[2fr_3fr]">
              <p className="font-semibold">Attribute filter:</p>
              <div className="flex flex-col gap-2">
                <div>
                  <p>Brand:</p>
                  <p className="text-txtBody">
                    BRI, BNI, BCA, Bank Tabungan Nasional
                  </p>
                </div>
                <div>
                  <p>Service Level:</p>
                  <p className="text-txtBody">
                    Kantor Cabang Utama, Kantor Cabang Pembantu
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
